import Component from '@glimmer/component'
import { colors } from '@colors'
import convertDateFormat from 'teacher-ui-client/utils/giraffe/convert-date-format'
import { formatNumber } from 'teacher-ui-client/utils/giraffe/formatter'
import { SummaryBox, type Stat } from 'teacher-ui-client/components/giraffe/summary-box'
import {
  SeriesColumn,
  type ChartSpacing,
  type SeriesColumnOptions,
} from 'teacher-ui-client/components/giraffe/primitives/series-column'
import type { LegendOptions } from 'highcharts'
import { SummaryBoxColorScheme } from 'teacher-ui-client/components/giraffe/summary-box/themes'
import LeftSplitChart from 'teacher-ui-client/components/giraffe/left-split-chart'
import { service } from '@ember/service'
import type { IntlService } from 'ember-intl'
import type { AwardsEarnedTotalsByGradeData } from 'teacher-ui-client/utils/student-events/fetchers/awards-earned-totals'

const computeGoldenEggs = (gold: number, silver: number, bronze: number) => {
  const totalSilver = gold * 3 + silver
  const totalBronze = totalSilver * 5 + bronze
  return totalBronze * 1000
}

interface Args {
  data: AwardsEarnedTotalsByGradeData
  grade: string
  summaryTitle: string
  title: string
  yAxisTitle: string
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class TrophiesTotals extends Component<Signature> {
  @service intl!: IntlService

  /**
   * Spacing for the SeriesColumn chart
   */
  readonly chartSpacing: ChartSpacing = [10, 10, 10, 10]

  /**
   * Colour scheme for the SummaryBox
   */
  readonly colorScheme: SummaryBoxColorScheme = SummaryBoxColorScheme.LightBlue

  /**
   * Chart legend for SeriesColumn
   */
  readonly legend: LegendOptions = { enabled: false }

  /**
   * List of category data, extracted from the input data.
   * Formats month data for Highcharts for use as column labels.
   * Converts month dates into short names.
   */
  get categories(): string[] {
    const months = this.args.data.months?.map((data) => data.month) ?? []

    return months.map((month) => convertDateFormat(month))
  }

  /**
   * List of column data, extracted from the input data.
   * Formats month data for Highcharts.
   */
  get columns(): SeriesColumnOptions[] {
    const monthData = this.args.data.months ?? []

    const goldenEggs = monthData.map(({ gold, silver, bronze }) => computeGoldenEggs(gold, silver, bronze))

    return [{ name: 'Golden Eggs', data: goldenEggs, color: colors.juicyOrange300, type: 'column' }]
  }

  /**
   * Computed list of summary statistics, extracted from the input data.
   * Display total gold, silver and bronze counts.
   */
  get summaryStats(): Stat[] {
    const { intl } = this
    const { gold, silver, bronze } = this.args.data.summary

    const goldLabel = intl.t('reporting.charts.awardsEarned.trophies.goldTrophy')
    const silverLabel = intl.t('reporting.charts.awardsEarned.trophies.silverMedallion')
    const bronzeLabel = intl.t('reporting.charts.awardsEarned.trophies.bronzeMedal')
    const totalLabel = intl.t('reporting.charts.awardsEarned.trophies.total')

    const total = gold + silver + bronze

    return [
      { label: goldLabel, value: formatNumber(gold, 0) },
      { label: silverLabel, value: formatNumber(silver, 0) },
      { label: bronzeLabel, value: formatNumber(bronze, 0) },
      { label: totalLabel, value: formatNumber(total, 0) },
    ]
  }

  <template>
    <div data-test-giraffe-awards-earned-trophies-totals class="h-full" ...attributes>
      <LeftSplitChart>
        <:left>
          <SummaryBox
            class="h-full"
            @title={{@summaryTitle}}
            @grade={{@grade}}
            @stats={{this.summaryStats}}
            @colorScheme={{this.colorScheme}}
          />
        </:left>
        <:right>
          <SeriesColumn
            @title={{@title}}
            @data={{this.columns}}
            @categories={{this.categories}}
            @stackingStyle="normal"
            @legend={{this.legend}}
            @yAxisLabel={{@yAxisTitle}}
            @yAxisReversedStacks={{false}}
            @chartSpacing={{this.chartSpacing}}
          />
        </:right>
      </LeftSplitChart>
    </div>
  </template>
}

export default TrophiesTotals
